import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import SetupPassword from './pages/SetupPassword';
import Homepage from './pages/Homepage';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import CandidateGraph from './pages/CandidateGraph';
import TargetedOutreach from './pages/TargetedOutreach';
import DeepResearch from './pages/DeepResearch';
import Performance from './pages/Performance';
import AdminConsole from './pages/AdminConsole';
import HelpSupport from './pages/HelpSupport';
import { authService } from './services/authService';

function getConfiguredOrigin(url) {
  if (!url) {
    return null;
  }

  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
}

function isSignalCueAppHost(hostname = window.location.hostname, origin = window.location.origin) {
  const configuredAppOrigin = getConfiguredOrigin(process.env.REACT_APP_APP_URL);

  return configuredAppOrigin === origin ||
         hostname === 'app.signalcue.ai' ||
         hostname.includes('app.signalcue.ai') ||
         hostname.includes('signalcue-web-staging') ||
         hostname.includes('signalcue-web-probe') ||
         hostname.includes('localhost') ||
         hostname.includes('127.0.0.1');
}

// Component to handle token from URL parameters (for cross-domain auth)
function AuthHandler({ children }) {
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    const userStr = urlParams.get('user');

    if (token && userStr) {
      // Store token and user in sessionStorage
      sessionStorage.setItem('signalcue_token', token);
      sessionStorage.setItem('signalcue_user', userStr);
      
      // Remove token from URL for security
      const newParams = new URLSearchParams(urlParams);
      newParams.delete('token');
      newParams.delete('user');
      
      const newSearch = newParams.toString();
      const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location]);

  return children;
}

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const location = useLocation();
  
  // Check for token in URL params first (for cross-domain auth)
  const urlParams = new URLSearchParams(location.search);
  const tokenFromUrl = urlParams.get('token');
  const userFromUrl = urlParams.get('user');
  
  // If token is in URL, store it immediately (synchronously) before checking auth
  if (tokenFromUrl && userFromUrl && !authService.isAuthenticated()) {
    sessionStorage.setItem('signalcue_token', tokenFromUrl);
    sessionStorage.setItem('signalcue_user', userFromUrl);
    // Remove token from URL for security
    const newParams = new URLSearchParams(urlParams);
    newParams.delete('token');
    newParams.delete('user');
    const newSearch = newParams.toString();
    const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
    window.history.replaceState({}, document.title, newUrl);
  }
  
  if (!authService.isAuthenticated()) {
    // Check if we're on the app domain - if so, redirect to public login
    // If we're already on the public domain, show login
    const isAppDomain = isSignalCueAppHost();
    
    if (isAppDomain) {
      // Redirect to public login site
      const loginUrl = process.env.REACT_APP_LOGIN_URL || (window.location.hostname.includes('-staging') ? window.location.origin : window.location.origin.replace('app.signalcue.ai', 'signalcue.ai'));
      window.location.href = `${loginUrl}/login`;
      return null;
    }
    // If on public domain, redirect to login route
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  // Check if we're on the public domain (signalcue.ai) vs app domain (app.signalcue.ai)
  // In production: signalcue.ai is public, app.signalcue.ai is app
  // In local dev: localhost:3000 could be either, but we'll treat it as app domain
  const hostname = window.location.hostname;
  const isLocalDev = hostname.includes('localhost') || hostname.includes('127.0.0.1');
  const isPublicDomain = hostname === 'signalcue.ai' || !isSignalCueAppHost(hostname);
  
  return (
    <BrowserRouter>
      <AuthHandler>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/setup-password" element={<SetupPassword />} />
          {isPublicDomain ? (
            // On public domain, show homepage or redirect authenticated users to app domain
            <>
              <Route path="/" element={<Homepage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route 
                path="*" 
                element={
                  <PublicDomainHandler />
                } 
              />
            </>
          ) : (
            // On app domain, show protected routes
            <>
              {isLocalDev && (
                <>
                  <Route path="/" element={<Homepage />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                </>
              )}
              <Route 
                path="/candidates" 
                element={
                  <ProtectedRoute>
                    <CandidateGraph />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/targeted-outreach" 
                element={
                  <ProtectedRoute>
                    <TargetedOutreach />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/deep-research" 
                element={
                  <ProtectedRoute>
                    <DeepResearch />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/performance" 
                element={
                  <ProtectedRoute>
                    <Performance />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <AdminConsole />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/help" 
                element={
                  <ProtectedRoute>
                    <HelpSupport />
                  </ProtectedRoute>
                } 
              />
              {!isLocalDev && <Route path="/" element={<Navigate to="/candidates" replace />} />}
            </>
          )}
        </Routes>
      </AuthHandler>
    </BrowserRouter>
  );
}

// Component to handle public domain - redirect authenticated users to app domain
function PublicDomainHandler() {
  const location = useLocation();
  
  useEffect(() => {
    // If authenticated and on public domain, redirect to app domain
    if (authService.isAuthenticated() && location.pathname !== '/login' && location.pathname !== '/') {
      const appUrl = process.env.REACT_APP_APP_URL || (window.location.hostname.includes('-staging') ? window.location.origin : window.location.origin.replace('signalcue.ai', 'app.signalcue.ai'));
      window.location.href = appUrl;
    }
  }, [location]);
  
  // If not on login or homepage, redirect to homepage
  if (location.pathname !== '/login' && location.pathname !== '/') {
    return <Navigate to="/" replace />;
  }
  
  return null;
}

export default App;
